import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Plus, X } from 'lucide-react';

export interface ComboItem {
  id: string;
  name: string;
  /** Linha secundária opcional (ex.: setor da empresa, tipo da originadora). */
  subtitle?: string;
}

/**
 * Combobox "buscar ou criar nova" reutilizável (E6 PR2) para Empresa / Originadora / Pessoa.
 * Filtro client-side sobre a lista já carregada (escala da Vega — ver useEntities). Quando
 * `onCreateNew` é passado e o texto digitado não casa exatamente com um item, oferece "Criar
 * <texto>", que o pai trata abrindo o mini-formulário específico da entidade.
 */
export function EntityCombobox({
  label,
  placeholder = 'Buscar ou criar nova…',
  items,
  loading = false,
  fetching = false,
  value,
  onSelect,
  onCreateNew,
  disabled = false,
}: {
  label: string;
  placeholder?: string;
  items: ComboItem[];
  loading?: boolean;
  /**
   * true durante qualquer busca da lista (inicial ou refetch em 2º plano). Só suprime "Criar …" —
   * não esconde os itens já em cache (isso fica a cargo de `loading`). Evita criar às cegas com uma
   * lista potencialmente desatualizada (ex.: reabrir o modal logo após criar a entidade em outro lugar).
   */
  fetching?: boolean;
  value: ComboItem | null;
  onSelect: (item: ComboItem | null) => void;
  onCreateNew?: (draft: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const term = query.trim().toLowerCase();
  const filtered = term ? items.filter((i) => i.name.toLowerCase().includes(term)) : items;
  const exact = items.some((i) => i.name.toLowerCase() === term);
  const canCreate = Boolean(onCreateNew) && term.length > 0 && !exact;
  const showCreate = canCreate && !fetching;

  // Achado F14 (revisão 2026-07): Esc não tinha handler próprio aqui -- borbulhava até o handler de
  // Esc do modal pai (window.addEventListener) e fechava a janela inteira em vez de só o dropdown.
  // stopPropagation() num elemento da árvore impede a chegada ao listener do window (diferente do
  // caso do F3, que era sobre MÚLTIPLOS listeners no MESMO alvo window -- aqui os alvos são
  // diferentes, a propagação normal pela árvore do DOM resolve).
  const listboxId = `${label.replace(/\s+/g, '-').toLowerCase()}-listbox`;

  return (
    <div
      ref={ref}
      className="relative"
      onKeyDown={(e) => {
        if (open && e.key === 'Escape') {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted">
        {label}
      </span>

      {value ? (
        <div className="flex items-center gap-2 rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px]">
          <span className="flex-1 truncate text-vega-ink">{value.name}</span>
          {!disabled && (
            <button
              type="button"
              aria-label={`Limpar ${label}`}
              onClick={() => {
                onSelect(null);
                setQuery('');
              }}
              className="text-vega-muted hover:text-vega-danger"
            >
              <X size={14} />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-2 rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-left text-[13px] text-vega-muted disabled:opacity-60"
        >
          <span className="flex-1">{placeholder}</span>
          <ChevronDown size={14} />
        </button>
      )}

      {open && !value && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-[90] rounded border border-vega-line bg-vega-surface p-1 shadow-vega-md">
          <input
            autoFocus
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Digite para filtrar…"
            className="mb-1 w-full rounded border border-vega-line bg-vega-surface px-2 py-1.5 text-[12.5px] text-vega-ink outline-none"
          />
          <div id={listboxId} role="listbox" aria-label={label} className="max-h-56 overflow-y-auto">
            {loading && <div className="px-2 py-2 text-[12px] text-vega-muted-2">Carregando…</div>}
            {!loading &&
              filtered.slice(0, 50).map((i) => (
                <button
                  type="button"
                  role="option"
                  key={i.id}
                  onClick={() => {
                    onSelect(i);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="flex w-full flex-col items-start rounded-[3px] px-2 py-1.5 text-left text-[12.5px] hover:bg-vega-bg-2"
                >
                  <span className="text-vega-ink">{i.name}</span>
                  {i.subtitle && <span className="text-[11px] text-vega-muted-2">{i.subtitle}</span>}
                </button>
              ))}
            {!loading && filtered.length === 0 && !canCreate && (
              <div className="px-2 py-2 text-[12px] text-vega-muted-2">Nada encontrado.</div>
            )}
            {!loading && canCreate && fetching && (
              <div className="px-2 py-2 text-[12px] text-vega-muted-2">Atualizando lista…</div>
            )}
            {showCreate && (
              <button
                type="button"
                onClick={() => {
                  onCreateNew?.(query.trim());
                  setOpen(false);
                }}
                className="flex w-full items-center gap-1.5 rounded-[3px] px-2 py-1.5 text-left text-[12.5px] text-vega-info hover:bg-vega-bg-2"
              >
                <Plus size={12} /> Criar “{query.trim()}”
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
